import { useState } from 'react';
import { Alert, Button, Card, Descriptions, Form, Input, Modal, Radio, Space, Tag, message } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormActions, FormField } from '../../shared/form';
import { PaymentConfigUpdateSchema } from '../../schemas/subscription';
import type { PaymentConfigUpdateDTO } from '../../schemas/subscription';
import { readPath, subscriptionRequest, useDraftIdempotency, useSubscriptionRead, writeOptions } from './api';
import { canUpdatePaymentConfig, dateText, PaymentConfigSchema } from './contracts';
import type { Capabilities, PaymentConfig } from './contracts';
import { ReadError } from './common';

function PaymentConfigForm({ brand, config, onClose, onRefresh, onUnconfirmed }: { brand: string; config: PaymentConfig; onClose: () => void; onRefresh: () => void; onUnconfirmed: (message: string) => void }) {
  const form = useForm<PaymentConfigUpdateDTO>({ resolver: zodResolver(PaymentConfigUpdateSchema), mode: 'onBlur', defaultValues: { purchaseEnabled: config.purchaseEnabled ?? undefined, reason: '' } });
  const idempotencyKey = useDraftIdempotency();
  const submit = async (values: PaymentConfigUpdateDTO) => {
    const request = { ...values, targetBrand: brand, expectedVersion: config.version };
    try {
      const saved = await subscriptionRequest('/api/admin/subscriptions/payment-config', PaymentConfigSchema, writeOptions(brand, { ...request, idempotencyKey: idempotencyKey(request) }, 'PUT'));
      if (saved.purchaseEnabled !== values.purchaseEnabled || saved.version <= config.version) throw new Error('伺服器未確認本次變更');
      message.success(`品牌 ${brand} 的購買設定已儲存`);
    } catch (error) {
      onUnconfirmed(`${error instanceof Error ? error.message : '操作結果尚未確認'}。已要求重新讀取設定，請核對最新狀態後再操作。`);
    } finally {
      // A failed response may follow a committed write. Discard the stale CAS
      // preview and read authoritative state before allowing another mutation.
      onRefresh();
      onClose();
    }
  };
  return <FormProvider {...form}><Form layout="vertical" onFinish={form.handleSubmit(submit)}>
    <Alert type="info" showIcon message={`目標品牌：${brand}`} description="此設定控制該品牌的新購買；開啟後仍需符合方案與支付渠道條件。" />
    <FormField<PaymentConfigUpdateDTO> name="purchaseEnabled" label="購買開關" required control={<Radio.Group><Space wrap><Radio value={true}>開啟購買</Radio><Radio value={false}>關閉購買</Radio></Space></Radio.Group>} />
    <FormField<PaymentConfigUpdateDTO> name="reason" label="變更原因" required control={<Input.TextArea rows={3} maxLength={500} showCount />} />
    <FormActions submitting={form.formState.isSubmitting} dirty={form.formState.isDirty} onCancel={onClose} submitLabel={config.purchaseEnabled === null ? '建立並儲存設定' : '儲存購買設定'} />
  </Form></FormProvider>;
}

export function PaymentConfigControl({ brand, capabilities, refresh }: { brand: string; capabilities: Capabilities | null; refresh: number }) {
  const [revision, setRevision] = useState(0);
  const [editing, setEditing] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const canRead = capabilities?.canReadPaymentConfig === true;
  const state = useSubscriptionRead(canRead ? readPath('/api/admin/subscriptions/payment-config', brand) : null, PaymentConfigSchema, refresh + revision);
  const config = state.data;
  const canWrite = canUpdatePaymentConfig(capabilities, config);
  return <Card title="品牌購買開關" extra={<Button disabled={!canRead || state.loading} onClick={() => { setEditing(false); setRevision(value => value + 1); }}>重新讀取設定</Button>} loading={canRead && state.loading}>
    {!capabilities ? <Alert type="info" message="正在驗證購買設定權限" /> : !canRead ? <Alert type="info" message="沒有讀取此品牌購買設定的權限" /> : <>
      <ReadError error={state.error} />
      {unconfirmed && <Alert type="warning" showIcon message="上次變更回覆未確認" description={unconfirmed} />}
      {config && <>
        <Descriptions column={1} items={[
          { key: 'brand', label: '目標品牌', children: brand },
          { key: 'enabled', label: '購買狀態', children: <Tag color={config.purchaseEnabled === true ? 'green' : config.purchaseEnabled === false ? 'orange' : undefined}>{config.purchaseEnabled === null ? '尚未設定' : config.purchaseEnabled ? '已開啟' : '已關閉'}</Tag> },
          { key: 'version', label: '版本', children: config.version },
          { key: 'updated', label: '更新時間', children: dateText(config.updatedAt) },
        ]} />
        {config.purchaseEnabled === null && <Alert type="warning" showIcon message="此品牌尚未設定購買開關" description="請由有權限的管理員明確選擇開啟或關閉並儲存；讀取頁面不會建立設定。" />}
        {canWrite ? <Button style={{ marginTop: 16 }} onClick={() => { setUnconfirmed(null); setEditing(true); }}>{config.purchaseEnabled === null ? '設定購買開關' : '調整購買開關'}</Button> : <Alert type="info" message="此購買設定目前僅供讀取" />}
      </>}
    </>}
    <Modal title={`品牌 ${brand} · 購買設定`} open={editing && canWrite && !state.loading} onCancel={() => setEditing(false)} footer={null} destroyOnClose maskClosable={false}>
      {editing && canWrite && config && <PaymentConfigForm key={`${brand}:${config.version}:${revision}`} brand={brand} config={config} onClose={() => setEditing(false)} onRefresh={() => setRevision(value => value + 1)} onUnconfirmed={setUnconfirmed} />}
    </Modal>
  </Card>;
}
